import { D as DialogExt } from './dialog-nybQUbi-.js';
import { b as bodyScrollLock } from './bodyScrollLock-BdpZ2Ojz.js';
import './pubsub-COBwjfLh.js';
import './notyf-DIw0RvBj.js';

class WelcomeScreen extends DialogExt {
  /** @type {number} */ #autoCloseTimeoutId = 0;
  /** @type {number} */ #openDelayTimeoutId = 0;
  /** @type {string} */ #dismissedCookieKey = 'welcome-screen-dismissed';
  /** @type {number} */ #autoCloseDelay = Number(this.dataset.autocloseDelay) || 0;
  /** @type {number} */ #openDelay = Number(this.dataset.openDelay) || 0;
  /** @type {string} */ #cookieMaxAge = this.dataset.cookieMaxAge || 'forever';
  /** @type {boolean} */ #testMode = this.hasAttribute('data-test-mode');
  /** @type {AbortController | undefined} */ #dismissAbortController;
  /** @type {AbortController | undefined} */ #themeEditorAbortController;

  connectedCallback() {
    if (super.connectedCallback()) return;

    if (!window.Shopify?.designMode) {
      this.#removeSectionFromDOM();
    }

    if (!this.#shouldMount()) {
      this.remove();
      return;
    }

    if (window.Shopify?.designMode) {
      this.#bindThemeEditor();
      return;
    }

    this.#bindBackdropDismiss();
    this.#startOpenDelayTimer();
  }

  disconnectedCallback() {
    this.#clearOpenDelayTimer();
    this.#clearAutoCloseTimer();
    this.#dismissAbortController?.abort();
    this.#themeEditorAbortController?.abort();
  }

  close() {
    this.#clearAutoCloseTimer();
    // Persist before trap deactivate so CTA navigation cannot race the cookie write.
    if (this.getAttribute('aria-hidden') !== 'true') {
      this.#persistDismissedState();
    }
    super.close();
    bodyScrollLock(false);
    if (!window.Shopify?.designMode) {
      this.remove();
    }
  }

  /**
   * Focus the CTA instead of the mobile close button when the dialog opens.
   * @returns {HTMLElement | undefined}
   */
  getInitialFocusElement() {
    return this.querySelector('[data-welcome-screen-initial-focus]') ?? undefined;
  }

  /**
   * Determine whether the welcome screen should stay in the DOM on connect.
   * @returns {boolean}
   */
  #shouldMount() {
    if (window.Shopify?.designMode || this.#testMode) return true;
    if (this.#isDismissed()) return false;
    return true;
  }

  /**
   * Route backdrop and Escape dismissal through close().
   * Focus-trap hides the dialog on its own and skips close(), so the
   * dismissal cookie is never written and the screen returns.
   * @returns {void}
   */
  #bindBackdropDismiss() {
    this.#dismissAbortController?.abort();
    this.#dismissAbortController = new AbortController();
    const { signal } = this.#dismissAbortController;

    window.addEventListener('mousedown', (event) => this.#onBackdropDismiss(event), {
      capture: true,
      signal,
    });
    window.addEventListener('touchstart', (event) => this.#onBackdropDismiss(event), {
      capture: true,
      passive: false,
      signal,
    });

    this.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'Escape' || this.getAttribute('aria-hidden') === 'true') return;
        this.close();
      },
      { signal }
    );
  }

  /**
   * Close when the press starts on this dialog's backdrop.
   * @param {Event} event
   * @returns {void}
   */
  #onBackdropDismiss(event) {
    if (this.getAttribute('aria-hidden') === 'true') return;

    const consent = this.#cookieConsentHost(event);
    if (consent) {
      // Mark before the focus trap's document listener treats this press as outside.
      consent.setAttribute('data-dialog-ignore-outside-click', '');
      return;
    }

    const backdrop = this.querySelector('.backdrop');
    const { target } = event;
    if (!(backdrop instanceof Element) || !(target instanceof Node) || !backdrop.contains(target)) return;

    event.preventDefault();
    this.close();
  }

  /**
   * @param {Event} event
   * @returns {Element | null}
   */
  #cookieConsentHost(event) {
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [event.target];
    const host = path.find((node) => node instanceof Element && node.localName === 'csm-cookie-consent');
    return host instanceof Element ? host : null;
  }

  /**
   * Start the timer that opens the dialog after the configured open delay.
   * @returns {void}
   */
  #startOpenDelayTimer() {
    this.#clearOpenDelayTimer();
    this.#openDelayTimeoutId = window.setTimeout(() => {
      this.open();

      if (!this.#testMode) {
        this.#startAutoCloseTimer();
      }
    }, this.#openDelay);
  }

  /**
   * Clear the pending open-delay timer, if any.
   * @returns {void}
   */
  #clearOpenDelayTimer() {
    if (!this.#openDelayTimeoutId) return;
    window.clearTimeout(this.#openDelayTimeoutId);
    this.#openDelayTimeoutId = 0;
  }

  /**
   * Start (or restart) the timer that auto-closes the dialog.
   * @returns {void}
   */
  #startAutoCloseTimer() {
    if (window.Shopify?.designMode || this.#autoCloseDelay <= 0) return;
    this.#clearAutoCloseTimer();
    this.#autoCloseTimeoutId = window.setTimeout(() => this.close(), this.#autoCloseDelay);
  }

  /**
   * Clear the pending auto-close timer, if any.
   * @returns {void}
   */
  #clearAutoCloseTimer() {
    if (!this.#autoCloseTimeoutId) return;
    window.clearTimeout(this.#autoCloseTimeoutId);
    this.#autoCloseTimeoutId = 0;
  }

  /**
   * Check whether the dismissal cookie is already set.
   * @returns {boolean}
   */
  #isDismissed() {
    const encodedKey = encodeURIComponent(this.#dismissedCookieKey);
    return document.cookie
      .split('; ')
      .some((entry) => entry.startsWith(`${encodedKey}=`) && entry.split('=')[1] === '1');
  }

  /**
   * Persist the dismissed state in a cookie so the welcome screen stays closed.
   * @returns {void}
   */
  #persistDismissedState() {
    if (window.Shopify?.designMode || this.#testMode) return;
    try {
      const encodedKey = encodeURIComponent(this.#dismissedCookieKey);
      const encodedValue = encodeURIComponent('1');
      // 'forever' has no real cookie equivalent, so fall back to a 10 year lifetime.
      const tenYearsInSeconds = 10 * 365 * 24 * 60 * 60;
      const maxAge =
        this.#cookieMaxAge === 'forever' ? tenYearsInSeconds : Number(this.#cookieMaxAge) || 0;

      document.cookie = `${encodedKey}=${encodedValue}; Path=/; SameSite=Lax; Max-Age=${maxAge}`;
    } catch (error) {
      console.error('Unable to persist welcome-screen dismissal state.', error);
    }
  }

  /**
   * Open on section select in the theme editor, close on deselect.
   * @returns {void}
   */
  #bindThemeEditor() {
    const sectionId = this.dataset.welcomeScreenSectionId;
    if (!sectionId) return;

    this.#themeEditorAbortController?.abort();
    this.#themeEditorAbortController = new AbortController();
    const { signal } = this.#themeEditorAbortController;

    document.addEventListener(
      'shopify:section:select',
      ({ detail }) => {
        if (detail.sectionId !== sectionId) return;
        this.open();
      },
      { signal }
    );

    document.addEventListener(
      'shopify:section:deselect',
      ({ detail }) => {
        if (detail.sectionId !== sectionId) return;
        this.close();
      },
      { signal }
    );

    document.addEventListener(
      'shopify:block:select',
      ({ detail }) => {
        if (detail.sectionId !== sectionId) return;
        this.open();
      },
      { signal }
    );

    document.addEventListener(
      'shopify:section:unload',
      ({ detail }) => {
        if (detail.sectionId !== sectionId) return;
        this.remove();
      },
      { signal }
    );
  }

  /**
   * Remove the welcome screen section wrapper from the DOM.
   * @returns {void}
   */
  #removeSectionFromDOM() {
    const sectionId = this.dataset.welcomeScreenSectionId;
    if (!sectionId) return;

    const section = document.getElementById(`shopify-section-${sectionId}`);
    if (section) {
      section.remove();
    }
  }
}

if (!customElements.get('welcome-screen')) {
  customElements.define('welcome-screen', WelcomeScreen);
}
